import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarCheck, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import BookDemoModal from "@/components/BookDemoModal";
import { getProduct, PRODUCTS } from "@/data/products";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const ProductDetail = () => {
    const { slug } = useParams();
    const product = getProduct(slug);
    const [demoOpen, setDemoOpen] = useState(false);

    if (!product) return <Navigate to="/products" replace />;

    const Icon = product.icon;
    const idx = PRODUCTS.findIndex((p) => p.slug === slug);
    const next = PRODUCTS[(idx + 1) % PRODUCTS.length];

    return (
        <>
            <Seo
                title={`${product.name} | Coaching Crafters AI`}
                description={`${product.name} — ${product.tagline} ${product.positioning}`}
                path={`/products/${product.slug}`}
            />

            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="product-hero">
                <div
                    className="pointer-events-none absolute -right-40 -top-40 h-[30rem] w-[30rem] rounded-full bg-ember/10 blur-3xl"
                    aria-hidden="true"
                />
                <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:py-20">
                    <Link
                        to="/products"
                        data-testid="back-to-products-link"
                        className="inline-flex items-center gap-2 text-sm font-medium text-ink/50 transition-colors hover:text-ember"
                    >
                        <ArrowLeft className="h-4 w-4" /> All products
                    </Link>
                    <div className="mt-8 grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
                        <div>
                            <p className="flex items-center gap-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ember">
                                <Icon className="h-4 w-4" /> Our Products · 0{idx + 1}
                            </p>
                            <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                                <MaskedLine delay={0.1}>{product.name}</MaskedLine>
                            </h1>
                            <Reveal delay={0.25}>
                                <p className="mt-4 font-serif text-xl italic text-ember">{product.tagline}</p>
                                <p className="mt-5 text-base leading-relaxed text-ink/60 sm:text-lg">
                                    {product.overview}
                                </p>
                                <div className="mt-8 flex flex-wrap gap-4">
                                    <button
                                        data-testid="product-book-demo-button"
                                        onClick={() => setDemoOpen(true)}
                                        className="inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 text-sm font-semibold text-bone shadow-[0_10px_30px_rgba(30,79,163,0.4)] transition-colors hover:bg-ember-dark"
                                    >
                                        <CalendarCheck className="h-4 w-4" />
                                        Book a Demo
                                    </button>
                                    <Link
                                        to="/contact"
                                        state={{ product: product.name }}
                                        data-testid="product-enquire-button"
                                        className="rounded-full border border-ink/15 px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:border-ember hover:text-ember"
                                    >
                                        Enquire Now
                                    </Link>
                                </div>
                            </Reveal>
                        </div>
                        <Reveal delay={0.3} className="relative">
                            <div className="absolute -inset-6 rounded-[2.5rem] bg-ember/10 blur-3xl" aria-hidden="true" />
                            <motion.div
                                whileHover={{ scale: 1.015 }}
                                transition={{ duration: 0.3 }}
                                className="relative overflow-hidden rounded-3xl border border-white/40 shadow-[0_40px_80px_-24px_rgba(26,25,24,0.35)]"
                            >
                                <img
                                    src={product.image}
                                    alt={product.imageAlt}
                                    className="aspect-[3/2] w-full object-cover"
                                    loading="eager"
                                    data-testid="product-hero-image"
                                />
                            </motion.div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* AT A GLANCE */}
            <section className="grain relative bg-obsidian" data-testid="product-glance-strip">
                <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10">
                    <p className="mb-8 text-center font-mono text-xs uppercase tracking-[0.25em] text-ember-soft">
                        Key specifications at a glance
                    </p>
                    <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5">
                        {product.glance.map((g, i) => (
                            <Reveal key={g.label} delay={i * 0.06} className="text-center">
                                <p className="font-serif text-2xl font-semibold text-[#F5F5F4] sm:text-3xl">
                                    {g.value}
                                </p>
                                <p className="mt-1 text-xs text-[#F5F5F4]/50">{g.label}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* KEY FEATURES */}
            <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-24" data-testid="product-features">
                <Reveal className="max-w-2xl">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">Key features</p>
                    <h2 className="mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl">
                        What {product.name} does best
                    </h2>
                </Reveal>
                <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {product.features.map((f, i) => (
                        <Reveal key={f.title} delay={(i % 3) * 0.08}>
                            <motion.div
                                whileHover={{ y: -6 }}
                                transition={{ duration: 0.25 }}
                                className="h-full rounded-3xl border border-[#E2E8F4] bg-white p-7 shadow-sm"
                            >
                                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-ember/10">
                                    <f.icon className="h-5 w-5 text-ember" />
                                </span>
                                <h3 className="mt-4 text-base font-semibold text-ink">{f.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-ink/55">{f.text}</p>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* BENEFITS */}
            <section className="border-y border-[#E2E8F4] bg-sand/60" data-testid="product-benefits">
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-24">
                    <Reveal className="max-w-2xl">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">Outcomes</p>
                        <h2 className="mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl">
                            What you get out of it
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {product.benefits.map((b, i) => (
                            <Reveal key={b.stat} delay={i * 0.08}>
                                <div className="h-full rounded-3xl border border-[#E2E8F4] bg-bone p-7">
                                    <p className="font-serif text-3xl font-semibold text-ember">{b.stat}</p>
                                    <p className="mt-3 text-sm leading-relaxed text-ink/60">{b.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO IT'S FOR */}
            <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-24" data-testid="product-audiences">
                <Reveal className="max-w-2xl">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">Who it's for</p>
                    <h2 className="mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl">
                        Built for teams like yours
                    </h2>
                </Reveal>
                <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {product.audiences.map((a, i) => (
                        <Reveal key={a.title} delay={i * 0.08}>
                            <motion.div
                                whileHover={{ y: -6 }}
                                transition={{ duration: 0.25 }}
                                className="h-full rounded-3xl border border-[#E2E8F4] bg-white p-7 shadow-sm"
                            >
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ember text-bone">
                                    <a.icon className="h-5 w-5" />
                                </span>
                                <h3 className="mt-4 text-base font-semibold text-ink">{a.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-ink/55">{a.text}</p>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* FULL SPECIFICATIONS */}
            <section className="border-y border-[#E2E8F4] bg-white/70" data-testid="product-specs">
                <div className="mx-auto max-w-5xl px-6 py-20 sm:px-10 lg:py-24">
                    <Reveal className="max-w-2xl">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">Full specifications</p>
                        <h2 className="mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl">
                            Every detail, on the record
                        </h2>
                        <p className="mt-3 text-sm text-ink/55">
                            The complete specification for {product.name}, grouped for quick scanning.
                        </p>
                    </Reveal>
                    <Reveal className="mt-10">
                        <Accordion
                            type="multiple"
                            defaultValue={["spec-0"]}
                            className="w-full"
                            data-testid="specs-accordion"
                        >
                            {product.specGroups.map((group, gi) => (
                                <AccordionItem
                                    key={group.title}
                                    value={`spec-${gi}`}
                                    className="mb-4 overflow-hidden rounded-2xl border border-[#E2E8F4] bg-bone px-6"
                                >
                                    <AccordionTrigger
                                        data-testid={`spec-group-${gi}`}
                                        className="py-5 text-left text-base font-semibold text-ink hover:text-ember hover:no-underline"
                                    >
                                        {group.title}
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-2">
                                        <dl className="divide-y divide-[#E2E8F4]">
                                            {group.rows.map(([term, desc]) => (
                                                <div
                                                    key={term}
                                                    className="grid grid-cols-1 gap-1 py-3.5 sm:grid-cols-3 sm:gap-6"
                                                >
                                                    <dt className="text-sm font-semibold text-ink/80">{term}</dt>
                                                    <dd className="text-sm leading-relaxed text-ink/60 sm:col-span-2">
                                                        {desc}
                                                    </dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            {/* HOW WE DELIVER */}
            <section className="grain relative bg-obsidian" data-testid="product-delivery">
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-24">
                    <Reveal className="max-w-2xl">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember-soft">How we deliver</p>
                        <h2 className="mt-4 font-serif text-3xl font-semibold text-[#F5F5F4] sm:text-4xl">
                            From first call to sign-off
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {product.delivery.map((d, i) => (
                            <Reveal key={d.step} delay={i * 0.08}>
                                <div className="h-full rounded-3xl border border-white/10 bg-obsidian-card p-7 transition-colors duration-300 hover:border-ember/50">
                                    <span className="font-serif text-4xl font-semibold text-ember/40">
                                        0{i + 1}
                                    </span>
                                    <h3 className="mt-4 text-base font-semibold text-[#F5F5F4]">{d.step}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#F5F5F4]/55">{d.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-4xl px-6 py-20 sm:px-10 lg:py-24" data-testid="product-faq">
                <Reveal className="text-center">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember">FAQ</p>
                    <h2 className="mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl">
                        {product.name}, questioned
                    </h2>
                </Reveal>
                <Reveal className="mt-10">
                    <Accordion type="single" collapsible className="w-full" data-testid="product-faq-accordion">
                        {product.faqs.map((f, i) => (
                            <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-[#E2E8F4]">
                                <AccordionTrigger
                                    data-testid={`product-faq-question-${i + 1}`}
                                    className="py-5 text-left text-base font-semibold text-ink hover:text-ember hover:no-underline"
                                >
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="pb-5 text-sm leading-relaxed text-ink/60">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
            </section>

            {/* CLOSING CTA */}
            <section className="grain relative overflow-hidden bg-obsidian" data-testid="product-cta">
                <div
                    className="pointer-events-none absolute left-1/2 top-1/2 h-[26rem] w-[26rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-ember/15 blur-3xl"
                    aria-hidden="true"
                />
                <div className="relative mx-auto max-w-4xl px-6 py-20 text-center sm:px-10">
                    <Reveal>
                        <h2 className="font-serif text-3xl font-semibold text-[#F5F5F4] sm:text-4xl">
                            See {product.name} <em className="italic text-ember-soft">in action.</em>
                        </h2>
                        <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-[#F5F5F4]/60">
                            Thirty minutes with a product specialist, tailored to your goals. Or keep browsing the
                            studio.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                            <button
                                data-testid="product-cta-demo-button"
                                onClick={() => setDemoOpen(true)}
                                className="inline-flex items-center gap-2 rounded-full bg-ember px-8 py-4 text-sm font-semibold text-bone transition-colors hover:bg-ember-dark"
                            >
                                <CalendarCheck className="h-4 w-4" />
                                Book a Demo
                            </button>
                            <Link
                                to={`/products/${next.slug}`}
                                data-testid="product-next-link"
                                className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-[#F5F5F4] transition-colors hover:border-ember hover:text-ember"
                            >
                                Next: {next.name}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <BookDemoModal open={demoOpen} onOpenChange={setDemoOpen} product={product.name} />
        </>
    );
};

export default ProductDetail;
